'use client'

import Link from 'next/link'

import SafeImage from '@/features/veluvit/components/ui/media-image'
import { cn } from '@/core/utils/common'

import { CARD_FX } from '@/features/veluvit/components/ui/card-fx'
import ProductMeta from '@/features/veluvit/components/ui/product-meta'
import WorkTitleOverlay from '@/features/veluvit/components/ui/work-title-overlay'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'
import { to } from '@/features/veluvit/lib/route'

/**
 * 홈 작품 카드(제타 · 크랙식) — 3:4 그림 + 그 아래 상품 공통 글 묶음(ProductMeta: 이름 · 좋아요 · 대화 수 · #태그 · 제작자), 카드 상자 없이.
 * - feed(기본): 격자용. rail: 가로 레일용(폭이 좁아 글이 한 단계 작다). rank 를 주면 그림 왼쪽 아래에 큰 순위 숫자(포인트 색).
 * 그림 위엔 배지를 얹지 않는다 — 대화 수는 글 묶음에, 신작 · 급상승 표시(NEW · HOT → N · H)는 "못생겼다"로 뺐다(2026-10-07).
 */
export default function FeedCard({
  id,
  priority,
  variant = 'feed',
  rank,
}: {
  id: string
  priority?: boolean
  variant?: 'feed' | 'rail'
  rank?: number
}) {
  const { catalog, thumb } = useCatalogMedia()
  const c = catalog.chars[id]
  if (!c) return null
  const rail = variant === 'rail'
  return (
    <Link
      href={to(`/c/${id}`)}
      className={cn(
        'group block min-w-0 text-t1 no-underline',
        rail && 'w-34 flex-none over10:w-44'
      )}
    >
      <span
        className={cn(
          'relative block aspect-[3/4] overflow-hidden rounded-panel bg-surface',
          CARD_FX
        )}
      >
        <SafeImage
          src={thumb(id)}
          alt=""
          fill
          sizes={
            rail
              ? '(min-width: 1001px) 176px, 136px'
              : '(min-width: 1001px) 220px, 45vw'
          }
          preload={priority}
          className="object-cover object-[50%_25%] transition-transform duration-500 ease-smooth group-hover:scale-[1.04]"
          appearance="plain"
        />
        <WorkTitleOverlay
          src={catalog.images[id]?.logo}
          sizes={rail ? '176px' : '220px'}
        />
        {rank !== undefined && (
          <span className="absolute inset-x-0 bottom-0 bg-[linear-gradient(0deg,rgb(0_0_0/0.75),transparent)] px-2.5 pt-8 pb-1.5 text-c32 leading-none font-bold text-point italic [text-shadow:0_2px_8px_#000]">
            {rank}
          </span>
        )}
      </span>
      <ProductMeta c={c} size={rail ? 'sm' : 'md'} />
    </Link>
  )
}
